import { useGetDailyPnlQuery } from '../../../../../apis/statistics/statistics-api.api.ts'
import { DashboardChartBox } from '../dashboard-chart-box.tsx'
import ReactECharts, { EChartsOption } from 'echarts-for-react'
import { TopLevelFormatterParams } from 'echarts/types/dist/shared'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '../../../../../redux/hooks.ts'
import { selectDashboardInput } from '../../../../../redux/slices/dashboard-input.slice.ts'
import echartsTheme from '../../../../../styles/echarts/echarts-theme.json'
import dayjs from 'dayjs'
import { selectThemeStyle } from '../../../../../features/setting/setting.slice.ts'

interface CumulativePnlChartProps {
  bookId: string
}

export const CumulativePnlChart: React.FC<CumulativePnlChartProps> = ({
  bookId,
}) => {
  const { t } = useTranslation()
  const themeStyle = useAppSelector(selectThemeStyle)
  const dashboardInput = useAppSelector(selectDashboardInput)
  const { data } = useGetDailyPnlQuery({
    bookId: bookId,
    startDate: dayjs(dashboardInput.dateRange[0]).format('YYYY-MM-DD'),
    endDate: dayjs(dashboardInput.dateRange[1]).format('YYYY-MM-DD'),
  })

  if (!data) return <></>

  // Ensure data has at least 20 entries
  const requiredLength = 20
  const missingLength = requiredLength - data.data.personalDailyPnls.length

  // Create a new array based on the original data
  const extendedData = [...data.data.personalDailyPnls]

  if (missingLength > 0) {
    const firstDate =
      data.data.personalDailyPnls.length > 0
        ? new Date(data.data.personalDailyPnls[0].date)
        : new Date()
    for (let i = 1; i <= missingLength; i++) {
      const newDate = new Date(firstDate)
      newDate.setDate(firstDate.getDate() - i)
      extendedData.unshift({
        date: newDate.toISOString().split('T')[0], // Format date as YYYY-MM-DD
        cumulativePnl: 0,
        pnl: 0,
        returnOnBP: 0,
        returnOnGMV: 0,
        drawdown: 0,
      })
    }
  }

  const labelCount = 30 // desired number of labels
  const interval = Math.max(1, Math.floor(extendedData.length / labelCount))

  const option: EChartsOption = {
    grid: {
      left: 0,
      right: 0,
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: extendedData.map((d) => d.date),
      axisLabel: {
        color: themeStyle.sumText,
        rotate: 60,
        interval,
        formatter: (value: string, index: number) =>
          index % 2 === 0 ? value : '',
      },
    },
    yAxis: [
      {
        type: 'value',
        name: 'PnL ($)',
        nameTextStyle: {
          color: themeStyle.sumText,
        },
        axisLabel: {
          color: themeStyle.sumText,
        },
      },
    ],
    legend: {
      data: ['Cumulative PnL', 'Drawdown'],
      selected: {
        'Cumulative PnL': true,
      },
      top: 'bottom',
    },
    tooltip: {
      trigger: 'axis',
      extraCssText: 'text-align: left;',
      formatter: (
        params: TopLevelFormatterParams | TopLevelFormatterParams[]
      ) => {
        const paramArr = Array.isArray(params) ? params : [params]
        const axisValue =
          (paramArr[0] as { axisValue?: string; axisValueLabel?: string })
            .axisValueLabel ??
          (paramArr[0] as { axisValue?: string; axisValueLabel?: string })
            .axisValue ??
          (paramArr[0] as { value?: string | number }).value ??
          ''
        let tooltipText = `${axisValue}<br/>`
        paramArr.forEach((param) => {
          const value = (param as { value?: string | number }).value
          const marker = (param as { marker?: string }).marker || ''
          const seriesName = (param as { seriesName?: string }).seriesName || ''

          tooltipText += `${marker} ${seriesName}: $${Number(
            value
          ).toLocaleString(undefined, { maximumFractionDigits: 0 })}<br/>`
        })
        return tooltipText
      },
    },
    series: [
      {
        type: 'line',
        name: 'Cumulative PnL',
        data: extendedData.map((d) => d.cumulativePnl.toFixed(0)),
      },
      {
        type: 'bar',
        name: 'Drawdown',
        data: extendedData.map((d) => d.drawdown.toFixed(0)),
        itemStyle: {
          color: '#f26466',
        },
      },
    ],
  }

  return (
    <DashboardChartBox title={t('dashboard.cumulative_pnl')}>
      <ReactECharts
        option={option}
        style={{
          height: '400px',
        }}
        theme={echartsTheme.themeName}
      />
    </DashboardChartBox>
  )
}
